import type { ApiClient } from "@positive/api-client";
import { createContext, useContext } from "react";

/** A saved view of a list ("variant", Fiori) as the store returns it. */
export interface ListViewRecord {
  id: string;
  listKey: string;
  name: string;
  /** The grid layout (`GridLayout` JSON). */
  layout: unknown;
  /** Visible to every user of the organisation. */
  shared: boolean;
  /** The current user's default view of the list. */
  isDefault: boolean;
  /** Created by the current user (only own views — or shared ones with the manage permission — can be changed). */
  own: boolean;
  /** Who created it (shown for shared views). */
  ownerName?: string | null;
}

export interface ListViewInput {
  name: string;
  layout: unknown;
  shared: boolean;
  isDefault: boolean;
}

/**
 * Where saved views live (UX8): the Platform API per user (`/api/platform/me/list-views`) in the app, this browser's
 * storage where no API is wired (samples, tests).
 */
export interface ListViewStore {
  /** May share views with everyone and change shared views (`platform.list_views.manage`). */
  readonly canShare: boolean;
  list(listKey: string, signal?: AbortSignal): Promise<ListViewRecord[]>;
  create(listKey: string, input: ListViewInput): Promise<ListViewRecord>;
  update(id: string, input: ListViewInput): Promise<ListViewRecord>;
  remove(id: string): Promise<void>;
  /** Sets (or with `null` clears, meaning "Standard") the user's default view of a list. */
  setDefault(listKey: string, viewId: string | null): Promise<void>;
}

const localKey = (listKey: string) => `positive.gridViews.${listKey}`;

function readLocal(listKey: string): ListViewRecord[] {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(localKey(listKey)) ?? "[]");
    return Array.isArray(parsed) ? (parsed as ListViewRecord[]) : [];
  } catch {
    return [];
  }
}

function writeLocal(listKey: string, views: readonly ListViewRecord[]): void {
  try {
    window.localStorage.setItem(localKey(listKey), JSON.stringify(views));
  } catch {
    // Storage unavailable: views live for this page only.
  }
}

/** Views kept in this browser only (no API wired). */
export function createLocalListViewStore(): ListViewStore {
  const listOf = new Map<string, string>();
  return {
    canShare: false,
    list: (listKey) => {
      const views = readLocal(listKey);
      views.forEach((view) => listOf.set(view.id, listKey));
      return Promise.resolve(views);
    },
    create: (listKey, input) => {
      const views = readLocal(listKey).map((view) => (input.isDefault ? { ...view, isDefault: false } : view));
      const record: ListViewRecord = { id: crypto.randomUUID(), listKey, ...input, shared: false, own: true };
      writeLocal(listKey, [...views, record]);
      listOf.set(record.id, listKey);
      return Promise.resolve(record);
    },
    update: (id, input) => {
      const listKey = listOf.get(id) ?? "";
      let updated: ListViewRecord | undefined;
      const views = readLocal(listKey).map((view) => {
        if (view.id !== id) return input.isDefault ? { ...view, isDefault: false } : view;
        updated = { ...view, ...input, shared: false };
        return updated;
      });
      writeLocal(listKey, views);
      return updated ? Promise.resolve(updated) : Promise.reject(new Error("not_found"));
    },
    remove: (id) => {
      const listKey = listOf.get(id) ?? "";
      writeLocal(
        listKey,
        readLocal(listKey).filter((view) => view.id !== id),
      );
      return Promise.resolve();
    },
    setDefault: (listKey, viewId) => {
      writeLocal(
        listKey,
        readLocal(listKey).map((view) => ({ ...view, isDefault: view.id === viewId })),
      );
      return Promise.resolve();
    },
  };
}

/** Wire shape of `GET /api/platform/me/list-views` (docs/contracts/lists-and-concurrency.md §5). */
interface ListViewDto {
  id: string;
  listKey: string;
  name: string;
  layout: unknown;
  shared: boolean;
  isDefault: boolean;
  own: boolean;
  ownerName?: string | null;
}

const toRecord = (dto: ListViewDto): ListViewRecord => ({ ...dto });

/** Saved views stored per user in the Platform module (`platform.list_view`, tenant RLS). */
export function createServerListViewStore(client: ApiClient, canShare: boolean): ListViewStore {
  const base = "platform/me/list-views";
  return {
    canShare,
    list: async (listKey, signal) =>
      (await client.get<ListViewDto[]>(base, { query: { listKey }, ...(signal ? { signal } : {}) })).map(toRecord),
    create: async (listKey, input) => toRecord((await client.post<ListViewDto>(base, { listKey, ...input })).data),
    update: async (id, input) => toRecord((await client.put<ListViewDto>(`${base}/${id}`, input)).data),
    remove: async (id) => {
      await client.delete(`${base}/${id}`);
    },
    setDefault: async (listKey, viewId) => {
      await client.put(`${base}/default`, { listKey, viewId });
    },
  };
}

const fallbackStore = createLocalListViewStore();

export const ListViewStoreContext = createContext<ListViewStore>(fallbackStore);

export function useListViewStore(): ListViewStore {
  return useContext(ListViewStoreContext);
}
